import {
  first, get, defer, cloneDeep,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  createTopZPosition,
} from '@discoveryedu/titan-grid-layout';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_HEIGHT,
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_HEIGHT,
  MODAL_CANVAS_PLACE_MODULE_BLEED,
} from '@/lib/constants';
import {
  assetModuleDefault,
  uploadModuleDefault,
} from '@/lib/constants/moduleDefaults';
import { bus } from '@/lib/eventBus';
import placeModuleWithoutGrid from '@/lib/utils/placeModule';
import { prepareReplacementOptions } from '@/lib/utils/moduleShapeOptions';
import canvasUtils from '@/mixins/canvasUtils';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';
import { assetIsUpload } from '@/lib/utils';

export default {
  mixins: [canvasUtils],
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
      'getModulesByModalIdOrderedByZPosition',
      'modulesOrderedByZPosition',
      'activeCanvasName',
      'currentModalCanvasId',
      'modalCanvases',
    ]),
    modalCanvas() {
      return this.modalCanvases.find((modal) => modal.id === this.currentModalCanvasId);
    },
    modalCanvasWidth() {
      return this.modalCanvas?.options?.width || MODAL_CANVAS_WIDTH;
    },
    modalCanvasHeight() {
      return this.modalCanvas?.options?.height || MODAL_CANVAS_HEIGHT;
    },
    destinationCanvasName() {
      // The AddModule component has a specified canvas name
      if (this.canvasName) return this.canvasName;

      // Otherwise read from Pinia to decide the current canvas
      return this.currentModalCanvasId
        ? 'modalModules'
        : (this.activeCanvasName || 'modules');
    },
    gridIsOff() {
      return this.destinationCanvasName !== 'notes';
    },
    modulesOrderedByZ() {
      if (this.destinationCanvasName === 'modalModules') {
        return this.getModulesByModalIdOrderedByZPosition(this.currentModalCanvasId);
      }

      return this.modulesOrderedByZPosition;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.REPLACE_MODULE,
      types.ADD_UPDATE_DELETE_MODULES,
    ]),
    selectModulesOnCanvas(moduleIds) {
      defer(() => {
        bus.emit('grid-layout:set-selection', {
          canvasName: this.destinationCanvasName,
          ids: moduleIds,
        });

        if (this.destinationCanvasName === 'notes') {
          defer(() => {
            // NotesCanvas "auto-scroll" is disabled. If it needs to auto-scroll
            // it needs to be done by emitting this event-bus id.
            bus.emit('notes-canvas:scroll-selection-into-view');
          });
        }

        if (moduleIds?.length === 1) {
          bus.emit('grid-item:focus', {
            canvasName: this.destinationCanvasName,
            gridItemId: first(moduleIds),
          });
        }
      });
    },
    async addModulesFromMyContent(assets) {
      if (!assets.length) return;

      const moduleIds = await assets.reduce(async (accPromise, asset) => {
        const acc = await accPromise;
        // Users can create other assets like TEAs or Boards,
        // but those are not really "uploads"
        const isUpload = assetIsUpload(asset);

        const mod = isUpload
          ? uploadModuleDefault(this.gridIsOff)
          : assetModuleDefault(this.gridIsOff, asset);
        mod.options.asset_id = asset.id;

        const moduleId = await this.placeModule(mod, false);
        return [...acc, moduleId];
      }, Promise.resolve([]));

      this.selectModulesOnCanvas(moduleIds);
    },
    async addModuleToState(mod, layoutOverrides, selectOnCanvas = true) {
      let newModuleId;
      const moduleToAdd = {
        ...mod,
        layout: {
          ...mod.layout,
          ...layoutOverrides,
        },
      };

      if (this.moduleToReplace) {
        newModuleId = await this[types.REPLACE_MODULE]({
          canvasName: this.destinationCanvasName,
          payload: moduleToAdd,
          replacedModuleId: this.moduleToReplace.id,
        });
      } else {
        newModuleId = await this[types.ADD_MODULE]({
          canvasName: this.destinationCanvasName,
          payload: moduleToAdd,
        });
      }

      if (selectOnCanvas) {
        this.selectModulesOnCanvas([newModuleId]);
      }

      return newModuleId;
    },
    async syncNotesActualHeights() {
      const notes = get(this, 'currentPage.notes', []);
      if (!notes.length) return;

      const items = document.querySelectorAll('.notes-canvas__auto-stack-item[data-module-id]');
      if (!items.length) return;

      const updateModules = [];
      items.forEach((itemEl) => {
        if (!itemEl.offsetHeight) return; // notes panel is closed (display: none)

        const moduleId = itemEl.getAttribute('data-module-id');
        const note = notes.find((m) => m.id === moduleId);
        if (!note) return;

        const moduleEl = itemEl.querySelector('.notes-canvas__auto-stack-module');
        if (!moduleEl) return;

        // scrollHeight reports full content height including overflow beyond explicit height
        // heightToPixels(h) = 48h - 16, so inverse is ceil((px + 16) / 48)
        const newHeight = Math.ceil((moduleEl.scrollHeight + 16) / 48);
        if (newHeight === (note.layout?.height ?? 1)) return;

        const mod = cloneDeep(note);
        mod.layout.height = newHeight;
        updateModules.push(mod);
      });

      if (!updateModules.length) return;

      await this[types.ADD_UPDATE_DELETE_MODULES]({
        canvasName: 'notes',
        updateModules,
        addModules: [],
        canUndo: false,
      });
    },
    async placeModule(mod, selectOnCanvas = true) {
      let newModuleId;
      if (this.moduleToReplace) {
        // If we have a module we're replacing, we already
        // know the destination coordinates.
        // Handle shape options appropriately based on module type.
        newModuleId = await this.addModuleToState(
          {
            ...mod,
            options: prepareReplacementOptions(mod, this.moduleToReplace),
          },
          this.moduleToReplace.layout,
          selectOnCanvas,
        );
      } else if (this.destinationCanvasName !== 'notes') {
        let canvasInfo;

        if (this.destinationCanvasName === 'modalModules') {
          canvasInfo = {
            width: this.modalCanvasWidth,
            height: this.modalCanvasHeight,
            bleed: MODAL_CANVAS_PLACE_MODULE_BLEED,
          };
        } else {
          canvasInfo = {
            width: EDITOR_CANVAS_WIDTH,
            height: this.currentPage?.options?.height || EDITOR_CANVAS_HEIGHT,
          };
        }

        canvasInfo.defaultPosition = this.getVisibleCanvasCenter(mod);

        const coordinates = placeModuleWithoutGrid(
          this.modulesOrderedByZ,
          mod,
          canvasInfo,
        );

        const layoutOverrides = {
          x_position: coordinates.x,
          y_position: coordinates.y,
        };

        // Assign the next z coordinate above other modules.
        layoutOverrides.z_position = createTopZPosition({
          itemsOrderedByZ: this.modulesOrderedByZ,
          zProperty: 'layout.z_position',
        });

        newModuleId = await this.addModuleToState(
          mod,
          layoutOverrides,
          selectOnCanvas,
        );
      } else {
        await this.syncNotesActualHeights();
        const notes = get(this, 'currentPage.notes', []);
        const nextY = notes.reduce(
          (acc, m) => Math.max(acc, (m.layout?.y_position ?? 0) + (m.layout?.height ?? 1)),
          0,
        );
        newModuleId = await this.addModuleToState(
          mod,
          {
            x_position: mod.layout.x_position,
            y_position: nextY,
          },
          selectOnCanvas,
        );
      }

      // Close the menu now that the module has been added to the canvas
      this.menuVisible = false;

      return newModuleId;
    },
  },
};
